import { useEffect, useState } from "react";
import { Users, Mail, Trash2, Send, Loader2, Copy, Check, Crown } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Team() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [copiedFor, setCopiedFor] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const requests = [api.get("/team/members")];
      if (isOwner) requests.push(api.get("/team/invites"));
      const [membersRes, invitesRes] = await Promise.all(requests);
      setMembers(membersRes.data);
      if (invitesRes) setInvites(invitesRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [isOwner]);

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post("/team/invites", { email: email.trim() });
      toast.success(`Invited ${data.email}`);
      setEmail("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/team/invites/${id}`);
      toast.success("Invite revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeMember = async (id) => {
    if (!confirm("Remove this teammate? They'll lose access to your campaigns.")) return;
    try {
      await api.delete(`/team/members/${id}`);
      toast.success("Team member removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyDevLink = async (link, id) => {
    await navigator.clipboard.writeText(link);
    setCopiedFor(id);
    toast.success("Link copied");
    setTimeout(() => setCopiedFor(null), 1600);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600">
            Workspace
          </span>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-2" data-testid="team-heading">
            Team
          </h1>
          <p className="text-slate-500 mt-1">
            {isOwner
              ? "Invite teammates so they can help manage your QR campaigns."
              : "You're a member of this workspace."}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Members */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-7">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-slate-500" />
                <h2 className="text-lg font-bold text-slate-900">Team members</h2>
                <span className="ml-auto text-xs font-bold uppercase tracking-wider text-slate-400">
                  {members.length}
                </span>
              </div>

              <ul className="divide-y divide-slate-100" data-testid="team-members-list">
                {members.map((m) => (
                  <li
                    key={m.id}
                    className="py-3 flex items-center gap-3"
                    data-testid={`team-member-${m.email}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 text-white flex items-center justify-center text-sm font-bold">
                      {(m.name || m.email).charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{m.name}</div>
                      <div className="text-xs text-slate-500 truncate">{m.email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {m.role === "owner" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-bold border border-blue-200">
                          <Crown className="w-3 h-3" /> OWNER
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-bold">
                          MEMBER
                        </span>
                      )}
                      {isOwner && m.id !== user.id && m.role !== "owner" && (
                        <button
                          onClick={() => removeMember(m.id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Remove"
                          data-testid={`remove-member-${m.email}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Invites (owner only) */}
            {isOwner && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-7">
                <div className="flex items-center gap-2 mb-4">
                  <Mail className="w-5 h-5 text-slate-500" />
                  <h2 className="text-lg font-bold text-slate-900">Invite by email</h2>
                </div>

                <form onSubmit={invite} className="space-y-3">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                      Teammate's email
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="invite-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="teammate@company.com"
                        className="h-11 rounded-xl flex-1"
                      />
                      <Button
                        type="submit"
                        disabled={inviting}
                        data-testid="invite-submit-btn"
                        className="h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4"
                      >
                        {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Invite</>}
                      </Button>
                    </div>
                  </div>
                </form>

                <div className="mt-6">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                    Pending invites ({invites.length})
                  </div>
                  {invites.length === 0 ? (
                    <div className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-xl">
                      No pending invites.
                    </div>
                  ) : (
                    <ul className="space-y-2" data-testid="pending-invites-list">
                      {invites.map((inv) => (
                        <li
                          key={inv.id}
                          className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200"
                          data-testid={`pending-invite-${inv.email}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">
                              {inv.email}
                            </div>
                            <div className="text-xs text-slate-500">
                              Sent {new Date(inv.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          {inv.dev_link && (
                            <button
                              onClick={() => copyDevLink(inv.dev_link, inv.id)}
                              className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                              title="Copy invite link (dev mode)"
                              data-testid={`copy-invite-link-${inv.email}`}
                            >
                              {copiedFor === inv.id ? (
                                <Check className="w-4 h-4 text-emerald-600" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => revoke(inv.id)}
                            className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Revoke"
                            data-testid={`revoke-invite-${inv.email}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
